// Session management for Sovereign MUSE.
// Real multi-session CRUD backed by the gateway's /api/sessions REST endpoints.
// Phase 2e: replaces the Phase 2a synthetic stub.

import { useState, useCallback, useEffect, useRef } from 'react';
import { getStoredToken } from './useAuth';
import { useAuth } from './useAuth';
import type { Project, Session } from '../auth/types';
import { carryDraft, clearDraft } from '../continue-fresh/drafts';

export type { Session } from '../auth/types';

function buildHeaders(): Record<string, string> {
  const token = getStoredToken();
  return {
    'Authorization': `Bearer ${token ?? ''}`,
    'Content-Type': 'application/json',
  };
}

// Keep in sync with SESSION_ID_RE in src/gateway/push/sender.ts.
const SESSION_ID_RE = /^sess_[0-9a-f-]{36}$/;

/** Session id from a notification deep link (`/?session=sess_…`), or null if absent/malformed. */
export function sessionIdFromUrl(url: string): string | null {
  try {
    const id = new URL(url, window.location.origin).searchParams.get('session');
    return id && SESSION_ID_RE.test(id) ? id : null;
  } catch {
    return null;
  }
}

/**
 * Cold start from a notification tap: read the linked session id out of the
 * address bar and strip ONLY that param (other params and history.state stay).
 */
function consumeLinkedSessionId(): string | null {
  const id = sessionIdFromUrl(window.location.href);
  if (id) {
    const url = new URL(window.location.href);
    url.searchParams.delete('session');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }
  return id;
}

export function useSession() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [archivedSessions, setArchivedSessions] = useState<Session[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  // During the short archive→fresh-session handoff there is deliberately no
  // active chat id. Keep the companion identity explicit so the shell cannot
  // visually or behaviorally fall back to a different companion meanwhile.
  const [holdingCompanionIdentity, setHoldingCompanionIdentity] = useState<string | null>(null);
  const projectMoveLocks = useRef(new Set<string>());
  const [pendingProjectMoveSessionIds, setPendingProjectMoveSessionIds] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [authFailed, setAuthFailed] = useState(false);
  // Synchronous mirror of authFailed. loadSessions() needs to know *immediately*
  // (same tick, not next render) whether the create-session failure it just saw
  // was a 401 so it can pick the right sessionError message — React state updates
  // are batched and wouldn't be visible via closure until the next render.
  const authFailedRef = useRef(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  // Increment to re-trigger the load effect (retry after session-create failure).
  const [loadRetryCount, setLoadRetryCount] = useState(0);
  const lastFetchedArchivedRef = useRef<Session[]>([]);

  const fetchSessions = useCallback(async (): Promise<Session[]> => {
    if (!getStoredToken()) return [];
    lastFetchedArchivedRef.current = [];
    authFailedRef.current = false;
    setAuthFailed(false);
    // Retry up to 3 times before evicting the token — a cold-starting gateway
    // returns transient 401s that would otherwise wipe a perfectly valid session.
    const delays = [0, 500, 1500];
    for (let attempt = 0; attempt < delays.length; attempt++) {
      const delay = delays[attempt];
      const isLastAttempt = attempt === delays.length - 1;
      if (delay > 0) {
        await new Promise(r => setTimeout(r, delay));
      }
      try {
        const res = await fetch('/api/sessions?archived=all', { headers: buildHeaders() });
        if (res.status === 401) {
          if (!isLastAttempt) continue;
          authFailedRef.current = true;
          setAuthFailed(true);
          return [];
        }
        if (!res.ok) return [];
        const data = (await res.json()) as Session[];
        const recent = data.filter((session) => !session.archived);
        const archived = data.filter((session) => Boolean(session.archived));
        lastFetchedArchivedRef.current = archived;
        setSessions(recent);
        setArchivedSessions(archived);
        return recent;
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  const createSession = useCallback(async (opts: {
    type?: string;
    label?: string;
    companion?: string;
    /** "New chat in this project" — tagged server-side in the same INSERT. */
    projectId?: string;
  } = {}): Promise<Session | null> => {
    if (!getStoredToken()) return null;
    try {
      const res = await fetch('/api/sessions', {
        method: 'POST',
        headers: buildHeaders(),
        // PATH B SLICE 4: include companion so the gateway creates the session
        // under the picker's chosen identity instead of always defaulting to
        // DEFAULT_IDENTITY ('rook'). Omitting it (opts.companion undefined,
        // e.g. the Cmd+Shift+N shortcut which bypasses the picker) preserves
        // today's behavior exactly — server.ts's resolveCompanion() defaults
        // absent/undefined to DEFAULT_IDENTITY.
        body: JSON.stringify({ type: opts.type, label: opts.label, companion: opts.companion, project_id: opts.projectId }),
      });
      if (res.status === 401) {
        authFailedRef.current = true;
        setAuthFailed(true);
        return null;
      }
      if (!res.ok) return null;
      const session = (await res.json()) as Session;
      const recentSession = { ...session, archived: 0 };
      setSessions((prev) => [recentSession, ...prev.filter((row) => row.id !== session.id)]);
      setArchivedSessions((prev) => prev.filter((row) => row.id !== session.id));
      setActiveSessionId(session.id);
      setHoldingCompanionIdentity(null);
      setSessionError(null);
      return recentSession;
    } catch {
      return null;
    }
  }, []);

  const retrySessionInit = useCallback(() => {
    setSessionError(null);
    setLoading(true);
    setLoadRetryCount((n) => n + 1);
  }, []);

  // Load sessions on mount / when auth state changes
  useEffect(() => {
    if (!user) {
      setSessions([]);
      setArchivedSessions([]);
      setActiveSessionId(null);
      setHoldingCompanionIdentity(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function loadSessions() {
      try {
        const data = await fetchSessions();
        if (cancelled) return;
        // Open the chat a tapped notification points at; the param is
        // stripped so a reload doesn't pin the app to it.
        const linkedId = consumeLinkedSessionId();
        const linked = linkedId ? data.find((session) => session.id === linkedId) : undefined;
        if (data.length > 0) {
          // Fresh success — clear any sessionError left over from a prior failed
          // attempt in this same mount (e.g. a transient 401 during retry) so a
          // stale error message can't linger after things actually recovered.
          setSessionError(null);
          setActiveSessionId((linked ?? data[0]).id); // linked chat, else most recent (gateway orders by last_activity DESC)
          setHoldingCompanionIdentity(null);
        } else {
          // New user: no sessions exist yet. Create one so activeSessionId resolves
          // to a real id before useChat connects — prevents a double-connection where
          // connect(null) fires first and then connect(realId) fires again once the
          // REST create response arrives.
          const mostRecentArchived = lastFetchedArchivedRef.current[0];
          const created = await createSession(mostRecentArchived ? {
            type: mostRecentArchived.session_type,
            companion: mostRecentArchived.identity,
          } : undefined);
          if (cancelled) return;
          if (created) {
            setSessionError(null);
          } else {
            // createSession swallows its own errors and returns null on any failure
            // (token missing, gateway down, fetch error, invalid/expired token).
            // Without this guard the activeSessionId stays null permanently, useChat
            // never connects, and the user sees a blank app with no explanation.
            //
            // authFailedRef is checked (not the `authFailed` state) because it's
            // updated synchronously the instant a 401 is seen — the state value
            // wouldn't be visible via this closure until next render. Distinguishing
            // the message matters: a stale/mismatched bearer token is NOT a
            // connectivity problem, and telling the user to "check your connection"
            // sends them looking in the wrong place.
            setSessionError(
              authFailedRef.current
                ? 'Your access token is invalid or expired. Re-enter it to continue.'
                : 'Could not create a session. Check your connection and try again.',
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadSessions();
    return () => { cancelled = true; };
  // loadRetryCount is intentionally included: incrementing it re-runs this effect
  // on explicit retry without changing user/fetchSessions/createSession identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fetchSessions, createSession, loadRetryCount]);

  // ?companion= must match the session's OWN identity (Session.identity —
  // server-authoritative, see auth/types.ts). Omitting it defaults to 'rook'
  // server-side (resolveCompanionFromQuery) and 404s via checkSessionOwnership
  // for any rainer-owned session — same root cause as the useKeepsakes fix.
  // Sourced from local `sessions` state (not a client-guessable default)
  // since useSession already owns the session list.
  const companionQuery = useCallback(
    (sessionId: string): string => {
      const identity = [...sessions, ...archivedSessions].find((s) => s.id === sessionId)?.identity;
      return identity ? `?companion=${encodeURIComponent(identity)}` : '';
    },
    [sessions, archivedSessions],
  );

  const deleteSession = useCallback(async (sessionId: string) => {
    if (!getStoredToken()) return;
    try {
      const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}${companionQuery(sessionId)}`, {
        method: 'DELETE',
        headers: buildHeaders(),
      });
      if (!res.ok) {
        // Do NOT strip the session from local state on failure — it still
        // exists on the server. Optimistically removing it here was a
        // false-delete: the UI showed the conversation gone while the
        // server still had it.
        console.error('[useSession] delete failed:', res.status);
        return;
      }
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setArchivedSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setActiveSessionId((prev) => {
        if (prev !== sessionId) return prev;
        // Switch to next most-recent session
        const remaining = sessions.filter((s) => s.id !== sessionId);
        return remaining[0]?.id ?? null;
      });
    } catch (err) {
      console.error('[useSession] delete failed:', err);
    }
  }, [sessions, companionQuery]);

  const archiveSession = useCallback(async (sessionId: string) => {
    if (!getStoredToken()) return;
    const original = sessions.find((session) => session.id === sessionId);
    if (!original) return;
    try {
      const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}${companionQuery(sessionId)}`, {
        method: 'PATCH',
        headers: buildHeaders(),
        body: JSON.stringify({ archived: true }),
      });
      if (!res.ok) throw new Error(`archive failed (${res.status})`);
      const updated = { ...(await res.json()) as Session, archived: 1 };

      if (activeSessionId === sessionId) {
        const nextSameCompanion = sessions.find(
          (session) => session.id !== sessionId && session.identity === original.identity,
        );
        // React batches these synchronous state writes: the archived row and
        // old active id disappear in the same render. There is no render where
        // activeSession can resolve to an archived row.
        setSessions((prev) => prev.filter((session) => session.id !== sessionId));
        setArchivedSessions((prev) => [updated, ...prev.filter((session) => session.id !== sessionId)]);
        if (nextSameCompanion) {
          setActiveSessionId(nextSameCompanion.id);
          setHoldingCompanionIdentity(null);
        } else {
          // No cross-companion fallback. Enter an explicit no-target holding
          // state before awaiting POST; the shell retains the correct companion
          // identity while useChat receives no session id at all.
          setActiveSessionId(null);
          setHoldingCompanionIdentity(original.identity);
          const fresh = await createSession({
            type: original.session_type,
            companion: original.identity,
          });
          if (!fresh) {
            setSessionError('Conversation archived, but a fresh conversation could not be created. Try again.');
          }
        }
      } else {
        setSessions((prev) => prev.filter((session) => session.id !== sessionId));
        setArchivedSessions((prev) => [updated, ...prev.filter((session) => session.id !== sessionId)]);
      }
    } catch (err) {
      console.error('[useSession] archive failed:', err);
      // No optimistic mutation occurs before PATCH success, so failure leaves
      // the active row, identity, and both shelves byte-for-byte coherent.
    }
  }, [activeSessionId, companionQuery, createSession, sessions]);

  const restoreSession = useCallback(async (sessionId: string) => {
    if (!getStoredToken()) return;
    const original = archivedSessions.find((session) => session.id === sessionId);
    if (!original) return;
    const recent = { ...original, archived: 0 };

    setArchivedSessions((prev) => prev.filter((session) => session.id !== sessionId));
    setSessions((prev) => [recent, ...prev.filter((session) => session.id !== sessionId)]);
    try {
      const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}${companionQuery(sessionId)}`, {
        method: 'PATCH',
        headers: buildHeaders(),
        body: JSON.stringify({ archived: false }),
      });
      if (!res.ok) throw new Error(`restore failed (${res.status})`);
      const updated = (await res.json()) as Session;
      setSessions((prev) => prev.map((session) => session.id === sessionId ? updated : session));
    } catch (err) {
      console.error('[useSession] restore failed:', err);
      setSessions((prev) => prev.filter((session) => session.id !== sessionId));
      setArchivedSessions((prev) => [original, ...prev.filter((session) => session.id !== sessionId)]);
    }
  }, [archivedSessions, companionQuery]);

  const renameSession = useCallback(async (sessionId: string, label: string) => {
    if (!getStoredToken()) return;
    try {
      const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}${companionQuery(sessionId)}`, {
        method: 'PATCH',
        headers: buildHeaders(),
        body: JSON.stringify({ label }),
      });
      if (!res.ok) {
        console.error('[useSession] rename failed:', res.status);
        return;
      }
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, label } : s)));
    } catch (err) {
      console.error('[useSession] rename failed:', err);
    }
  }, [companionQuery]);

  const togglePin = useCallback(async (sessionId: string) => {
    if (!getStoredToken()) return;
    // Optimistic: flip pinned in local state immediately
    let previousPinned: number | undefined;
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        previousPinned = s.pinned;
        return { ...s, pinned: s.pinned ? 0 : 1 };
      }),
    );
    const newPinned = previousPinned === 0 || previousPinned === undefined;
    try {
      const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}${companionQuery(sessionId)}`, {
        method: 'PATCH',
        headers: buildHeaders(),
        body: JSON.stringify({ pinned: newPinned }),
      });
      if (!res.ok) throw new Error('patch failed');
    } catch (err) {
      console.error('[useSession] togglePin failed:', err);
      // Rollback on failure
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId ? { ...s, pinned: previousPinned ?? 0 } : s,
        ),
      );
    }
  }, [companionQuery]);

  const assignSessionProject = useCallback(async (
    sessionId: string,
    project: Project | null,
  ): Promise<boolean> => {
    const original = sessions.find((session) => session.id === sessionId);
    if (!getStoredToken() || !original) return false;

    // One in-flight project mutation per session. Ref-backed locking makes the
    // refusal synchronous, including two clicks before React can re-render.
    if (projectMoveLocks.current.has(sessionId)) return false;

    const previousProjectId = original.project_id;
    const nextProjectId = project?.id ?? null;
    if (previousProjectId === nextProjectId) return true;

    projectMoveLocks.current.add(sessionId);
    setPendingProjectMoveSessionIds(new Set(projectMoveLocks.current));

    setSessions((current) => current.map((session) => (
      session.id === sessionId ? { ...session, project_id: nextProjectId } : session
    )));
    try {
      const response = await fetch(
        `/api/sessions/${encodeURIComponent(sessionId)}${companionQuery(sessionId)}`,
        {
          method: 'PATCH',
          headers: buildHeaders(),
          body: JSON.stringify({ project_id: nextProjectId }),
        },
      );
      if (!response.ok) throw new Error(`project assignment failed (${response.status})`);
      const updated = await response.json() as Session;
      if (updated.identity !== original.identity || updated.project_id !== nextProjectId) {
        throw new Error('project assignment response mismatch');
      }
      // Deliberately update only project_id. Identity and the active target are
      // never accepted back from this organization-only mutation.
      setSessions((current) => current.map((session) => (
        session.id === sessionId ? { ...session, project_id: nextProjectId } : session
      )));
      return true;
    } catch (cause) {
      console.error('[useSession] project assignment failed:', cause);
      setSessions((current) => current.map((session) => (
        session.id === sessionId ? { ...session, project_id: previousProjectId } : session
      )));
      return false;
    } finally {
      projectMoveLocks.current.delete(sessionId);
      setPendingProjectMoveSessionIds(new Set(projectMoveLocks.current));
    }
  }, [companionQuery, sessions]);

  // WR session-scoped documents: merge-only local update after a successful
  // PUT /api/sessions/:id/document — mirrors useProjects.ts's
  // applyProjectUpdate exactly (never a parallel local copy; the central
  // `sessions` list is the one source WritingRoomLayer's props derive from,
  // so a switch is visible everywhere that reads `activeSession` without a
  // second fetch). No network call here — the caller already made one.
  const applySessionDocumentUpdate = useCallback((
    sessionId: string,
    documentPath: string,
    documentType: string,
  ) => {
    setSessions((current) => current.map((session) => (
      session.id === sessionId
        ? { ...session, document_path: documentPath, document_type: documentType }
        : session
    )));
  }, []);

  // ADR-022 Continue fresh. The old chat's unsent draft is copied into the
  // successor immediately but only cleared once the successor is the rendered
  // active chat (effect below), so a failed switch never loses the draft.
  const pendingDraftClearRef = useRef<{ fromId: string; toId: string } | null>(null);
  useEffect(() => {
    const pending = pendingDraftClearRef.current;
    if (pending && activeSessionId === pending.toId) {
      clearDraft(pending.fromId);
      pendingDraftClearRef.current = null;
    }
  }, [activeSessionId]);

  const openContinuation = useCallback((fromId: string, successor: Session) => {
    carryDraft(fromId, successor.id);
    pendingDraftClearRef.current = { fromId, toId: successor.id };
    setSessions((prev) => [successor, ...prev.filter((row) => row.id !== successor.id)]);
    setActiveSessionId(successor.id);
    setHoldingCompanionIdentity(null);
    setSessionError(null);
  }, []);

  /** Navigate to an existing successor (the "Continued in" card). The caller
   *  passes the row it found, so a successor that is archived (and so absent
   *  from `sessions`) is still reachable. */
  const followContinuation = useCallback((fromId: string, successor: Session) => {
    openContinuation(fromId, successor);
  }, [openContinuation]);

  /** Ask the server to continue `sessionId` in a fresh chat and switch to it. */
  const continueFresh = useCallback(async (sessionId: string): Promise<
    { ok: true; session: Session } | { ok: false; reason: 'rate_limited' | 'failed' }
  > => {
    if (!getStoredToken()) return { ok: false, reason: 'failed' };
    try {
      const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/continue${companionQuery(sessionId)}`, {
        method: 'POST',
        headers: buildHeaders(),
      });
      if (res.status === 429) return { ok: false, reason: 'rate_limited' };
      if (!res.ok) return { ok: false, reason: 'failed' };
      const successor = (await res.json()) as Session;
      openContinuation(sessionId, { ...successor, archived: 0 });
      return { ok: true, session: successor };
    } catch {
      return { ok: false, reason: 'failed' };
    }
  }, [companionQuery, openContinuation]);

  const switchSession = useCallback((sessionId: string) => {
    if (sessions.some((session) => session.id === sessionId)) {
      setActiveSessionId(sessionId);
      setHoldingCompanionIdentity(null);
      setSessionError(null);
    }
  }, [sessions]);

  // Warm start: the service worker relays a tapped notification's URL to the
  // already-open window. Subscribes once; the membership check reads the
  // latest list through a ref.
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      // Keep the 'muse-open-url' literal in sync with public/sw.js.
      if (event.data?.type !== 'muse-open-url' || typeof event.data.url !== 'string') return;
      const id = sessionIdFromUrl(event.data.url);
      if (!id) return;
      const open = () => {
        setActiveSessionId(id);
        setHoldingCompanionIdentity(null);
        setSessionError(null);
      };
      if (sessionsRef.current.some((session) => session.id === id)) {
        open();
        return;
      }
      // Unknown id: a wake may have created the session after our last load.
      // fetchSessions() writes the refetched list into `sessions` itself, so
      // activeSessionId can only ever be set to a row that is in the list.
      fetchSessions()
        .then((list) => { if (list.some((session) => session.id === id)) open(); })
        .catch(() => { /* intentional no-op: stay on the current chat */ });
    };
    const container = navigator.serviceWorker;
    container.addEventListener('message', onMessage);
    return () => container.removeEventListener('message', onMessage);
  }, [fetchSessions]);

  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;
  // Holding identity is valid only when there is deliberately no active target
  // during archive→create. If an explicit id fails to resolve to a current row,
  // expose no identity so the chat seam fails closed instead of masking the
  // incoherent target with stale state.
  const activeCompanionIdentity = activeSession?.identity
    ?? (activeSessionId === null ? holdingCompanionIdentity : null);

  return {
    sessions,
    archivedSessions,
    activeSession,
    activeSessionId,
    activeCompanionIdentity,
    loading,
    authFailed,
    sessionError,
    retrySessionInit,
    createSession,
    continueFresh,
    followContinuation,
    deleteSession,
    renameSession,
    switchSession,
    fetchSessions,
    togglePin,
    archiveSession,
    restoreSession,
    assignSessionProject,
    applySessionDocumentUpdate,
    pendingProjectMoveSessionIds,
  };
}
